import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../ui/Button';
import AvatarComposite from './AvatarComposite';
import {
  AVATAR_PROFILES,
  clearPart,
  fallbackSelection,
  getEnabledPaletteSwatches,
  getSelectedSwatch,
  normalizeSelection,
  randomizeSelection,
  selectPart,
  selectSwatch,
} from './avatarModel';

const MAX_UNDO = 20;
const AVATAR_TINT_MASK_RENDER_MODE = 'tint-mask';
const UPPER_BODY_SLOTS = new Set(['back_gear', 'clothes_base', 'jacket', 'neckwear', 'front_gear']);
const FEATURE_GROUP_DEFINITIONS = [
  { id: 'backgrounds', name: 'Backgrounds', slots: ['background'] },
  { id: 'face', name: 'Face', slots: ['body_base', 'skin_detail_low', 'nose', 'mouth', 'facial_hair', 'face_mark_left', 'face_mark_right'] },
  { id: 'eyes', name: 'Eyes', slots: ['eyes', 'irises', 'brows'] },
  { id: 'hair', name: 'Hair', slots: ['hair_back', 'hair_front'] },
  { id: 'clothes', name: 'Clothes', slots: ['clothes_base', 'trousers', 'shoes', 'jacket', 'neckwear'] },
  { id: 'extras', name: 'Extras', slots: ['back_gear', 'eyewear', 'face_mask', 'headwear', 'front_gear', 'portrait_fx'] },
];
const FEATURE_GROUP_BY_SLOT = new Map(FEATURE_GROUP_DEFINITIONS
  .flatMap(group => group.slots.map(slotKey => [slotKey, group.id])));

export function groupAvatarFeatures(features) {
  const groups = FEATURE_GROUP_DEFINITIONS.map(group => ({ ...group, features: [] }));
  const other = { id: 'other', name: 'Other', slots: [], features: [] };
  const groupsById = new Map(groups.map(group => [group.id, group]));

  [...(features || [])]
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
    .forEach(feature => {
      const groupId = FEATURE_GROUP_BY_SLOT.get(feature.key);
      (groupsById.get(groupId) || other).features.push(feature);
    });

  return [...groups, other].filter(group => group.features.length);
}

export function thumbnailRegionForSlot(slotKey) {
  if (slotKey === 'shoes') return 'feet';
  if (slotKey === 'trousers') return 'lower';
  if (UPPER_BODY_SLOTS.has(slotKey)) return 'upper';
  if (['background', 'body_base', 'skin_detail_low', 'portrait_fx'].includes(slotKey)) return 'full';
  return 'head';
}

function paletteGradient(swatches) {
  if (swatches.length === 1) return swatches[0].value;
  return `linear-gradient(to right, ${swatches.map((swatch, index) => (
    `${swatch.value} ${(index / (swatches.length - 1)) * 100}%`
  )).join(', ')})`;
}

function genericPreviewSelection(config, part) {
  const preview = selectPart(fallbackSelection(config), part, config);
  if (part.slotKey !== 'irises' || preview.slots.eyes) return preview;

  const eyes = (config?.parts || []).find(candidate => (
    candidate.slotKey === 'eyes' && candidate.state === 'published'
  ));
  return eyes ? selectPart(preview, eyes, config) : preview;
}

function AvatarPartPreview({ config, part }) {
  const region = thumbnailRegionForSlot(part.slotKey);
  const previewSelection = genericPreviewSelection(config, part);

  return (
    <span
      aria-hidden="true"
      className={`avatar-part-card__preview avatar-part-card__preview--${region}`}
      data-thumbnail-region={region}
    >
      <AvatarComposite
        creatorCard
        decorative
        devicePixelRatio={1}
        focus={region}
        renderProfile={AVATAR_PROFILES.PORTRAIT}
        selection={previewSelection}
      />
    </span>
  );
}

function trapDialogFocus(event, dialog) {
  if (event.key !== 'Tab' || !dialog) return;
  const controls = [...dialog.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])')];
  if (!controls.length) return;
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export default function AvatarCreatorWindow({
  config,
  selection,
  onClose,
  onSave,
  playerName,
}) {
  const dialogRef = useRef(null);
  const previousFocus = useRef(document.activeElement);
  const publishedParts = useMemo(() => (config?.parts || [])
    .filter(part => part.state === 'published'), [config]);
  const features = useMemo(() => {
    const populatedSlots = new Set(publishedParts.map(part => part.slotKey));
    return (config?.layerSlots || [])
      .filter(slot => populatedSlots.has(slot.key))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [config, publishedParts]);
  const featureGroups = useMemo(() => groupAvatarFeatures(features), [features]);
  const orderedFeatures = useMemo(() => featureGroups.flatMap(group => group.features), [featureGroups]);
  const [activeSlotKey, setActiveSlotKey] = useState(orderedFeatures[0]?.key || '');
  const [draft, setDraft] = useState(() => normalizeSelection(selection, config));
  const [history, setHistory] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (orderedFeatures.length && !orderedFeatures.some(feature => feature.key === activeSlotKey)) {
      setActiveSlotKey(orderedFeatures[0].key);
    }
  }, [activeSlotKey, orderedFeatures]);

  useEffect(() => {
    const restoreFocus = previousFocus.current;
    dialogRef.current?.querySelector('button')?.focus();
    return () => restoreFocus?.focus?.();
  }, []);

  const parts = useMemo(() => publishedParts
    .filter(part => part.slotKey === activeSlotKey)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)), [activeSlotKey, publishedParts]);
  const activeFeatureIndex = orderedFeatures.findIndex(feature => feature.key === activeSlotKey);
  const activeFeature = activeFeatureIndex >= 0 ? orderedFeatures[activeFeatureIndex] : null;
  const activePart = parts.find(part => draft.slots?.[activeSlotKey] === part.id) || null;
  const skinToneSwatches = activeSlotKey === 'body_base'
    && activePart?.renderMode === AVATAR_TINT_MASK_RENDER_MODE
    ? getEnabledPaletteSwatches(activePart, config)
    : [];
  const activeSkinTone = getSelectedSwatch(draft, activePart, config);
  const activeSkinToneIndex = Math.max(0, skinToneSwatches.findIndex(swatch => (
    swatch.id === activeSkinTone?.id
  )));
  const showWaistLayering = ['clothes_base', 'trousers'].includes(activeSlotKey)
    && orderedFeatures.some(feature => feature.key === 'clothes_base')
    && orderedFeatures.some(feature => feature.key === 'trousers');

  const handleFeatureKeyDown = (event, index) => {
    let nextIndex;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = Math.min(index + 1, orderedFeatures.length - 1);
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = Math.max(index - 1, 0);
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = orderedFeatures.length - 1;
    else return;

    event.preventDefault();
    setActiveSlotKey(orderedFeatures[nextIndex].key);
    dialogRef.current?.querySelectorAll('.avatar-creator__feature-button')[nextIndex]?.focus();
  };

  const selectAdjacentFeature = (offset) => {
    const nextFeature = orderedFeatures[activeFeatureIndex + offset];
    if (nextFeature) setActiveSlotKey(nextFeature.key);
  };

  const commit = (next) => {
    setHistory(current => [...current.slice(-(MAX_UNDO - 1)), draft]);
    setDraft(next);
    setError('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && !isSaving) {
      event.preventDefault();
      onClose();
      return;
    }
    trapDialogFocus(event, dialogRef.current);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      await onSave({ ...draft, configRevision: config.configRevision });
    } catch (saveError) {
      const reason = saveError instanceof Error && saveError.message
        ? ` ${saveError.message}`
        : ' Try again.';
      setError(`We could not save this survivor.${reason} Your choices are still here.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="avatar-creator-backdrop pl-ui" onKeyDown={handleKeyDown}>
      <section
        aria-labelledby="avatar-creator-title"
        aria-modal="true"
        className="avatar-creator"
        ref={dialogRef}
        role="dialog"
      >
        <header className="avatar-creator__header">
          <div>
            <h2 id="avatar-creator-title">Create Your Survivor</h2>
            <p>Regrets are optional. So are teeth.</p>
          </div>
          <button aria-label="Close appearance editor" className="avatar-creator__close" onClick={onClose} type="button">×</button>
        </header>

        <div className="avatar-creator__preview">
          <AvatarComposite
            config={config}
            label={`${playerName || 'Operative'}'s customized full-body avatar`}
            renderProfile={AVATAR_PROFILES.PORTRAIT}
            selection={draft}
          />
        </div>

        <div className="avatar-creator__editor">
          <nav aria-label="Avatar features" className="avatar-creator__feature-rail">
            {featureGroups.map(group => (
              <section aria-labelledby={`avatar-feature-group-${group.id}`} className="avatar-creator__feature-group" key={group.id}>
                <h3 className="avatar-creator__feature-group-title" id={`avatar-feature-group-${group.id}`}>{group.name}</h3>
                <div className="avatar-creator__feature-list">
                  {group.features.map(feature => {
                    const index = orderedFeatures.findIndex(item => item.key === feature.key);
                    const selected = activeSlotKey === feature.key;
                    return (
                      <button
                        aria-controls="avatar-feature-panel"
                        aria-pressed={selected}
                        className={`avatar-creator__feature-button ${selected ? 'is-selected' : ''}`}
                        key={feature.key}
                        onClick={() => setActiveSlotKey(feature.key)}
                        onKeyDown={event => handleFeatureKeyDown(event, index)}
                        tabIndex={selected ? 0 : -1}
                        type="button"
                      >
                        <span>{feature.name}</span>
                        <span aria-hidden="true" className="avatar-creator__feature-marker">›</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </nav>

          <div className="avatar-creator__mobile-features">
            <label htmlFor="avatar-feature-select">Feature</label>
            <select
              className="avatar-creator__feature-select"
              disabled={!orderedFeatures.length}
              id="avatar-feature-select"
              onChange={event => setActiveSlotKey(event.target.value)}
              value={activeSlotKey}
            >
              {featureGroups.map(group => (
                <optgroup key={group.id} label={group.name}>
                  {group.features.map(feature => (
                    <option key={feature.key} value={feature.key}>{feature.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <div className="avatar-creator__feature-stepper">
              <button
                aria-label="Previous feature"
                disabled={activeFeatureIndex <= 0}
                onClick={() => selectAdjacentFeature(-1)}
                type="button"
              >
                ‹
              </button>
              <span aria-live="polite" role="status">
                {orderedFeatures.length ? `${activeFeatureIndex + 1} of ${orderedFeatures.length}` : 'No features'}
              </span>
              <button
                aria-label="Next feature"
                disabled={activeFeatureIndex < 0 || activeFeatureIndex >= orderedFeatures.length - 1}
                onClick={() => selectAdjacentFeature(1)}
                type="button"
              >
                ›
              </button>
            </div>
          </div>

          <section aria-labelledby="avatar-feature-title" className="avatar-creator__choices" id="avatar-feature-panel">
            <header className="avatar-creator__choices-header">
              <h3 id="avatar-feature-title">{activeFeature?.name || 'No features available'}</h3>
              <p>{activeFeature?.required ? 'Choose one required base.' : 'Choose one, or remove this layer.'}</p>
            </header>
            {showWaistLayering && (
              <label className="avatar-waist-layering">
                <span className="avatar-waist-layering__copy">
                  <strong>Shirt over trousers</strong>
                  <span>{draft.shirtOverTrousers ? 'Shirt covers the waistband.' : 'Trousers cover the shirt hem.'}</span>
                </span>
                <input
                  aria-label="Shirt over trousers"
                  checked={draft.shirtOverTrousers}
                  onChange={event => commit({ ...draft, shirtOverTrousers: event.target.checked })}
                  role="switch"
                  type="checkbox"
                />
                <span aria-hidden="true" className="avatar-waist-layering__track">
                  <span />
                </span>
              </label>
            )}
            <div className="avatar-creator__parts">
              {activeFeature && !activeFeature.required && (
                <button
                  aria-label={`None for ${activeFeature.name}`}
                  aria-pressed={!draft.slots?.[activeFeature.key]}
                  className={`avatar-part-card avatar-part-card--none ${!draft.slots?.[activeFeature.key] ? 'is-selected' : ''}`}
                  onClick={() => commit(clearPart(draft, activeFeature.key, config))}
                  type="button"
                >
                  <span aria-hidden="true" className="avatar-part-card__preview avatar-part-card__preview--none">×</span>
                  <strong>None</strong>
                  <span>Remove {activeFeature.name}</span>
                </button>
              )}
              {parts.length ? parts.map(part => {
                const selected = draft.slots?.[part.slotKey] === part.id;
                return (
                  <button
                    aria-pressed={selected}
                    className={`avatar-part-card ${selected ? 'is-selected' : ''}`}
                    key={part.id}
                    onClick={() => commit(selectPart(draft, part, config))}
                    type="button"
                  >
                    <AvatarPartPreview config={config} part={part} />
                    <strong>{part.name}</strong>
                    <span>{activeFeature?.name || part.slotKey}</span>
                  </button>
                );
              }) : (
                <p className="avatar-creator__empty">Nothing here yet. The shipment was mostly dust.</p>
              )}
            </div>
            {skinToneSwatches.length > 0 && activePart && activeSkinTone && (
              <fieldset
                className="avatar-skin-tone"
                style={{ '--avatar-skin-tone-gradient': paletteGradient(skinToneSwatches) }}
              >
                <legend>Skin tone</legend>
                <div className="avatar-skin-tone__value">
                  <span>Selected tone</span>
                  <output htmlFor="avatar-skin-tone-range">{activeSkinTone.label}</output>
                </div>
                <input
                  aria-label="Skin tone"
                  aria-valuetext={activeSkinTone.label}
                  className="avatar-skin-tone__range"
                  disabled={skinToneSwatches.length < 2}
                  id="avatar-skin-tone-range"
                  list="avatar-skin-tone-stops"
                  max={skinToneSwatches.length - 1}
                  min="0"
                  onChange={event => {
                    const swatch = skinToneSwatches[Number(event.target.value)];
                    if (swatch && swatch.id !== activeSkinTone.id) {
                      commit(selectSwatch(draft, activePart, swatch.id, config));
                    }
                  }}
                  step="1"
                  type="range"
                  value={activeSkinToneIndex}
                />
                <datalist id="avatar-skin-tone-stops">
                  {skinToneSwatches.map((swatch, index) => (
                    <option key={swatch.id} label={swatch.label} value={index} />
                  ))}
                </datalist>
                <div aria-hidden="true" className="avatar-skin-tone__ticks">
                  {skinToneSwatches.map(swatch => (
                    <span
                      className={swatch.id === activeSkinTone.id ? 'is-selected' : ''}
                      key={swatch.id}
                      style={{ backgroundColor: swatch.value }}
                    />
                  ))}
                </div>
              </fieldset>
            )}
          </section>
        </div>

        <footer className="avatar-creator__actions">
          <div className="avatar-creator__utilities">
            <Button onClick={() => commit(randomizeSelection(config))} variant="secondary">
              <span className="avatar-creator__action-label--desktop">Questionable Genetics</span>
              <span className="avatar-creator__action-label--mobile">Randomize</span>
            </Button>
            <Button
              disabled={!history.length}
              onClick={() => {
                const previous = history[history.length - 1];
                setHistory(current => current.slice(0, -1));
                setDraft(previous);
              }}
              variant="ghost"
            >
              <span className="avatar-creator__action-label--desktop">Put It Back</span>
              <span className="avatar-creator__action-label--mobile">Undo</span>
            </Button>
            <Button onClick={() => commit(fallbackSelection(config))} variant="ghost">Reset</Button>
          </div>
          <Button className="avatar-creator__save" disabled={isSaving} onClick={handleSave} variant="primary">
            {isSaving ? 'Saving…' : 'Looks Survivable'}
          </Button>
          {error && <p className="avatar-creator__error" role="alert">{error}</p>}
        </footer>
      </section>
    </div>
  );
}
